import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import LoadingOverlay from "../../../components/Ui/LoadingOverlay";
import Pagination from "../../../components/Ui/Paging";
import { calculatePage } from "../../../utils/paging";
import {
  adminGetIncidents,
  adminCreateIncident,
  adminAddIncidentUpdate,
  adminDeleteIncident,
  adminGetComponents,
} from "../../../services/statusAdmin";
import { IconPlus, IconTrash } from "../../../components/Ui/Icon";
import { formatDateToCustomString } from "../../../utils/utils";

const INCIDENT_STATUSES = ["investigating", "identified", "monitoring", "resolved"];
const IMPACT_LEVELS = ["none", "minor", "major", "critical"];

const INCIDENT_COLORS = {
  investigating: "bg-yellow-100 text-yellow-800",
  identified: "bg-red-100 text-red-800",
  monitoring: "bg-blue-100 text-blue-800",
  resolved: "bg-green-100 text-green-800",
};

const EMPTY_INCIDENT = { title: "", impact: "minor", components: [], updateBody: "" };
const EMPTY_UPDATE = { body: "", status: "investigating" };

export default function AdminStatusIncidents() {
  const [incidents, setIncidents] = useState([]);
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paging, setPaging] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });

  const [showCreate, setShowCreate] = useState(false);
  const [incidentForm, setIncidentForm] = useState(EMPTY_INCIDENT);

  const [expandedId, setExpandedId] = useState(null);
  const [updateForms, setUpdateForms] = useState({});

  const fetchIncidents = async (page = paging.page) => {
    setLoading(true);
    try {
      const res = await adminGetIncidents({ page, limit: paging.limit });
      setIncidents(res.data.data || []);
      setPaging((p) => ({
        ...p,
        page,
        total: res.data.total,
        totalPages: calculatePage(res.data.total, p.limit),
      }));
    } catch {
      toast.error("Failed to load incidents");
    }
    setLoading(false);
  };

  const fetchComponents = async () => {
    try {
      const res = await adminGetComponents();
      setComponents(res.data.data || []);
    } catch {}
  };

  useEffect(() => {
    fetchIncidents();
    fetchComponents();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateIncident = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminCreateIncident(incidentForm);
      toast.success("Incident created — subscribers have been notified");
      setShowCreate(false);
      setIncidentForm(EMPTY_INCIDENT);
      fetchIncidents(1);
    } catch {
      toast.error("Failed to create incident");
    }
    setLoading(false);
  };

  const handleAddUpdate = async (incidentId) => {
    const form = updateForms[incidentId];
    if (!form?.body || !form?.status) {
      toast.error("Body and status are required");
      return;
    }
    setLoading(true);
    try {
      await adminAddIncidentUpdate(incidentId, form);
      toast.success("Update posted — subscribers have been notified");
      setUpdateForms((prev) => ({ ...prev, [incidentId]: EMPTY_UPDATE }));
      fetchIncidents();
    } catch {
      toast.error("Failed to post update");
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this incident?")) return;
    setLoading(true);
    try {
      await adminDeleteIncident(id);
      toast.success("Deleted");
      fetchIncidents();
    } catch {
      toast.error("Delete failed");
    }
    setLoading(false);
  };

  return (
    <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
      <div className="flex w-full justify-between items-center p-3 flex-wrap">
        <h2 className="font-semibold text-3xl text-gray-900">Incidents</h2>
        <button
          onClick={() => { setIncidentForm(EMPTY_INCIDENT); setShowCreate(true); }}
          className="flex items-center gap-1 p-2 text-sm font-medium text-white bg-blue-700 rounded-lg hover:bg-blue-800"
        >
          <IconPlus /> New Incident
        </button>
      </div>

      <div className="p-3 space-y-3">
        {incidents.length === 0 && !loading && (
          <p className="text-gray-500 text-center py-8">No incidents found.</p>
        )}
        {incidents.map((inc) => (
          <div key={inc._id} className="border rounded-xl overflow-hidden">
            <div
              className="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer"
              onClick={() => setExpandedId(expandedId === inc._id ? null : inc._id)}
            >
              <div className="flex items-center gap-3">
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${INCIDENT_COLORS[inc.status]}`}>
                  {inc.status}
                </span>
                <span className="font-medium text-gray-900">{inc.title}</span>
                {inc.components?.length > 0 && (
                  <span className="text-xs text-gray-400">
                    [{inc.components.map((c) => c.name).join(", ")}]
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <span>{formatDateToCustomString(inc.createdAt)}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(inc._id); }}
                  className="text-white bg-slate-400 hover:bg-red-500 rounded px-2 py-1"
                >
                  <IconTrash className="size-3" />
                </button>
              </div>
            </div>

            {expandedId === inc._id && (
              <div className="px-4 py-3 space-y-3">
                {[...inc.updates].reverse().map((u, i) => (
                  <div key={i} className="border-l-4 border-blue-400 pl-3 py-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${INCIDENT_COLORS[u.status]}`}>
                        {u.status}
                      </span>
                      <span className="text-xs text-gray-400">{formatDateToCustomString(u.createdAt)}</span>
                    </div>
                    <p className="text-sm text-gray-700">{u.body}</p>
                  </div>
                ))}

                {inc.status !== "resolved" && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-3">
                    <p className="text-sm font-semibold text-gray-700 mb-2">Post Status Update</p>
                    <textarea
                      rows={3}
                      className="w-full border rounded px-3 py-2 text-sm mb-2"
                      placeholder="Describe the current situation…"
                      value={updateForms[inc._id]?.body || ""}
                      onChange={(e) =>
                        setUpdateForms((prev) => ({
                          ...prev,
                          [inc._id]: { ...(prev[inc._id] || EMPTY_UPDATE), body: e.target.value },
                        }))
                      }
                    />
                    <div className="flex gap-2 items-center">
                      <select
                        className="border rounded px-3 py-2 text-sm"
                        value={updateForms[inc._id]?.status || "investigating"}
                        onChange={(e) =>
                          setUpdateForms((prev) => ({
                            ...prev,
                            [inc._id]: { ...(prev[inc._id] || EMPTY_UPDATE), status: e.target.value },
                          }))
                        }
                      >
                        {INCIDENT_STATUSES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => handleAddUpdate(inc._id)}
                        className="px-4 py-2 bg-blue-700 text-white rounded text-sm hover:bg-blue-800"
                      >
                        Post Update &amp; Notify
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {paging.totalPages > 1 && (
        <Pagination
          totalPages={paging.totalPages}
          currentPage={paging.page}
          onPageChange={(p) => fetchIncidents(p)}
        />
      )}

      {loading && <LoadingOverlay />}

      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <form
            onSubmit={handleCreateIncident}
            className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg flex flex-col gap-4"
          >
            <h3 className="text-xl font-semibold">Create New Incident</h3>

            <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
              Title *
              <input
                className="border rounded px-3 py-2"
                value={incidentForm.title}
                onChange={(e) => setIncidentForm({ ...incidentForm, title: e.target.value })}
                required
              />
            </label>

            <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
              Impact
              <select
                className="border rounded px-3 py-2"
                value={incidentForm.impact}
                onChange={(e) => setIncidentForm({ ...incidentForm, impact: e.target.value })}
              >
                {IMPACT_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </label>

            <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
              Affected Components
              <select
                multiple
                className="border rounded px-3 py-2 h-28"
                value={incidentForm.components}
                onChange={(e) =>
                  setIncidentForm({
                    ...incidentForm,
                    components: Array.from(e.target.selectedOptions, (o) => o.value),
                  })
                }
              >
                {components.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
              <span className="text-xs text-gray-400">Hold Ctrl/Cmd to select multiple</span>
            </label>

            <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
              Initial Update *
              <textarea
                rows={3}
                className="border rounded px-3 py-2"
                placeholder="Describe what is happening…"
                value={incidentForm.updateBody}
                onChange={(e) => setIncidentForm({ ...incidentForm, updateBody: e.target.value })}
                required
              />
            </label>

            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 rounded border">
                Cancel
              </button>
              <button type="submit" className="px-4 py-2 rounded bg-blue-700 text-white hover:bg-blue-800">
                Create &amp; Notify Subscribers
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
